import { ReactNode, useState } from 'react';

import {
  IconButton as ButtonIcon,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography as Text,
} from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiMore2Fill,
  RiPencilLine,
} from '@remixicon/react';

import { DateUtils } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  ArtifactIcon,
  ArtifactListItem,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';

interface ExternalUrlArtifactListItemProps {
  artifact: AttachmentFragment;
  icon: ReactNode;
  subtitleLabel: string;
  openLabel: string;
  /** Optional UI URL. Omit it when the artifact has no navigable UI. */
  href?: string;
  createTimeFormat?: string;
  fallbackTitle?: (artifact: AttachmentFragment) => string;
  onDelete?: () => void;
  onRename?: () => void;
}

const defaultFallbackTitle = (artifact: AttachmentFragment) => artifact.uri;

export const ExternalUrlArtifactListItem = ({
  artifact,
  icon,
  subtitleLabel,
  openLabel,
  href,
  createTimeFormat = DateUtils.MONTH_DAY_FORMAT,
  fallbackTitle = defaultFallbackTitle,
  onDelete,
  onRename,
}: ExternalUrlArtifactListItemProps) => {
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    createTimeFormat,
  );

  const safeUrl = href && isSafeUrl(href) ? href : undefined;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const open = () => {
    if (safeUrl) {
      window.open(safeUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ArtifactListItem.Root onClick={safeUrl ? open : undefined}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body1" component="div" noWrap>
            {artifact.displayName || fallbackTitle(artifact)}
          </Text>
        }
        secondary={
          <Text variant="caption" component="div" color="textSecondary" noWrap>
            {subtitleLabel} &middot; {dateLabel}
          </Text>
        }
      />
      {(safeUrl || onRename || onDelete) && (
        <ArtifactListItem.Action>
          <ButtonIcon
            size="small"
            aria-label="Artifact actions"
            onClick={e => setMenuAnchorEl(e.currentTarget)}
          >
            <RiMore2Fill size={16} />
          </ButtonIcon>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            {safeUrl && (
              <MenuItem
                onClick={() => {
                  open();
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <RiExternalLinkLine />
                </ListItemIcon>
                {openLabel}
              </MenuItem>
            )}
            {onRename && (
              <MenuItem
                onClick={() => {
                  onRename();
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <RiPencilLine />
                </ListItemIcon>
                Rename
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                style={{ color: 'var(--essential-negative)' }}
                onClick={() => {
                  onDelete();
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <RiDeleteBinLine />
                </ListItemIcon>
                Delete
              </MenuItem>
            )}
          </Menu>
        </ArtifactListItem.Action>
      )}
    </ArtifactListItem.Root>
  );
};
